import {
  $, $document, $window, Layout, util
} from '@glu/core';
import template from './tableFooterLayout.hbs';

export default Layout.extend({
  template,

  appEvents() {
    const appEvents = {};

    if (this.hasStickyFooter) {
      appEvents.resize = 'changeFooterPosition';
    }

    return appEvents;
  },

  initialize() {
    this.grid = this.options.grid;
    this.hasStickyFooter = this.options.hasStickyFooter;

    if (this.hasStickyFooter) {
      this.changeFooterPosition = util.debounce(this.updateFooterPosition.bind(this), 20);
    }

    this.listenTo(this.grid, {
      'grid:columnWidthUpdated': this.handleGridContentRendered
    });
  },

  setBulkActions(view) {
    this.bulkActionsView = view;
  },

  setPager(view) {
    this.pagerView = view;
  },

  onRender() {
    this.showBulkActions();
    this.showPager();
    this.useStickyFooter();
  },

  showBulkActions() {
    if (this.bulkActionsView) {
      this.bulkActionsRegion.show(this.bulkActionsView);
    }
  },

  showPager() {
    if (this.pagerView) {
      this.pagerRegion.show(this.pagerView);
    }
  },

  handleGridContentRendered() {
    util.defer(() => {
      if (this.isClosed) {
        return;
      }
      this.updateFooterPosition();
    });
  },

  useStickyFooter() {
    if (!this.hasStickyFooter) {
      return;
    }

    this.addStickyFooterEvents();

    util.defer(() => {
      if (this.isClosed) {
        return;
      }
      this.initialGridBottomPadding = this.grid.$el.css('padding-bottom');
      this.initStickyScrollComponent();
      this.updateFooterPosition();
    });
  },

  updateFooterPosition() {
    if (this.isClosed || !this.hasStickyFooter) {
      return;
    }

    const $tableBody = this.grid.$('tbody[aria-live="polite"]');
    const $footer = this.$el;

    const footerHeight = $footer[0].offsetHeight;
    const { bottom, top } = $tableBody[0].getBoundingClientRect();
    const firstRowHeight = $tableBody.find('tr').height();
    const tableBottom = bottom + footerHeight;
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    const tableOutsideViewport = top + footerHeight + firstRowHeight > windowHeight;
    const tableBottomInViewport = tableBottom < windowHeight;

    if (tableOutsideViewport || tableBottomInViewport) {
      $footer.removeClass('sticky-footer');
      this.grid.$el.css('padding-bottom', this.initialGridBottomPadding);
      this.hideStickyScroll();
      return;
    }

    this.updateStickyScroll();
    $footer.addClass('sticky-footer');
    this.grid.$el.css('padding-bottom', footerHeight);
  },

  addStickyFooterEvents() {
    $window.on(`scroll.grid.${this.grid.cid}`, this.changeFooterPosition);

    if (!this.pagerView) {
      return;
    }

    // need to listen all collection events to update footer position
    this.listenTo(this.pagerView.inputCollection, 'add reset remove sync', this.changeFooterPosition, this);
  },

  initStickyScrollComponent() {
    const $tableLayout = this.grid.$('.table-layout');

    this.stickyScrollOptions = {
      $container: $tableLayout,
      left: 0,
      top: 0,
      bottom: 0,
      height: 0,
      width: 0,
      visible: true
    };
    this.$stickyScrollBar = this.initStickyScrollBar();
    this.updateStickyScroll();
    this.syncStickyScrollBarWithTable($tableLayout);
    this.addStickyScrollEvents();
  },

  initStickyScrollBar() {
    const $stickyScroll = $('<div class="grid-sticky-scroll"></div>'); // eslint-disable-line backbone/no-native-jquery
    $('<div/>').appendTo($stickyScroll).css({ // eslint-disable-line backbone/no-native-jquery
      width: `${this.stickyScrollOptions.$container.prop('scrollWidth')}px`
    });

    this.stickyScrollOptions.bottomPosition = this.$el.outerHeight() - 1; // use -1px from footer height to prevent showing content behind scroll bar.

    $stickyScroll.css({
      bottom: this.stickyScrollOptions.bottomPosition
    });
    return $stickyScroll.appendTo('body');
  },

  addStickyScrollEvents() {
    util.extend(this.stickyScrollOptions, {
      eventHandlers: [{
        $el: this.$stickyScrollBar,
        handlers: {
          'scroll.sticky': e => {
            if (this.stickyScrollOptions && this.stickyScrollOptions.visible) {
              this.syncTableWithStickyScroll($document.find(e.target));
            }
          }
        }
      }, {
        $el: this.stickyScrollOptions.$container,
        handlers: {
          'scroll.sticky': e => {
            if (this.stickyScrollOptions && !this.stickyScrollOptions.visible) {
              this.syncStickyScrollBarWithTable($document.find(e.target));
            }
          },
          'focusin.sticky': () => {
            util.defer(() => {
              this.syncStickyScrollBarWithTable(this.stickyScrollOptions.$container);
            });
          }
        }
      }]
    });

    util.each(this.stickyScrollOptions.eventHandlers, item => {
      util.each(item.handlers, (callback, event) => {
        item.$el.on(`${event}.${this.grid.cid}`, callback);
      });
    });
  },

  checkScrollVisibility() {
    if (this.isClosed) {
      return;
    }
    const { stickyScrollOptions } = this;
    const mustHide = (this.$stickyScrollBar.prop('scrollWidth') <= this.$stickyScrollBar.prop('offsetWidth'));
    // use -1px from footer height to prevent showing content behind scroll bar.
    const footerHeight = this.$el.outerHeight() - 1;

    if (stickyScrollOptions.visible === mustHide) {
      stickyScrollOptions.visible = !stickyScrollOptions.visible;
      // we cannot simply hide a sticky scroll bar
      // since its scrollLeft property will not update in that case
      this.$stickyScrollBar.toggleClass('grid-sticky-scroll-hidden');
    }

    // can be when screen is small and footer has simple pagination
    if (this.stickyScrollOptions.bottomPosition !== footerHeight) {
      this.stickyScrollOptions.bottomPosition = footerHeight;
      this.$stickyScrollBar.css({
        bottom: this.stickyScrollOptions.bottomPosition
      });
    }
  },

  syncStickyScrollBarWithTable($sender) {
    if (this.isClosed) {
      return;
    }
    this.$stickyScrollBar.prop('scrollLeft', $sender.prop('scrollLeft'));
  },

  syncTableWithStickyScroll($sender) {
    if (this.isClosed) {
      return;
    }
    this.stickyScrollOptions.$container.prop('scrollLeft', $sender.prop('scrollLeft'));
  },

  updateStickyScroll() {
    if (this.isClosed || !this.stickyScrollOptions) {
      return;
    }
    const { stickyScrollOptions } = this;
    const $tableLayout = stickyScrollOptions.$container;
    const offset = $tableLayout.offset();

    // No offset means no element.
    if (!offset) {
      return;
    }

    stickyScrollOptions.height = $tableLayout.outerHeight();
    stickyScrollOptions.width = $tableLayout.outerWidth();
    stickyScrollOptions.left = offset.left;
    stickyScrollOptions.top = offset.top;
    stickyScrollOptions.bottom = stickyScrollOptions.top + stickyScrollOptions.height;
    this.$stickyScrollBar.width(stickyScrollOptions.width).css('left', `${stickyScrollOptions.left}px`);
    this.$stickyScrollBar.find('div').width($tableLayout.prop('scrollWidth'));
    this.checkScrollVisibility();
  },

  // Remove a scrollbar and all related event handlers
  destroyStickyScrollElement() {
    if (!this.stickyScrollOptions) {
      return;
    }

    util.each(this.stickyScrollOptions.eventHandlers, item => {
      util.each(item.handlers, (callback, event) => {
        item.$el.off(`${event}.${this.grid.cid}`);
      });
    });
    this.stickyScrollOptions = {};
    this.$stickyScrollBar.remove();
  },

  hideStickyScroll() {
    if (this.isClosed || !this.stickyScrollOptions) {
      return;
    }
    this.stickyScrollOptions.visible = false;
    this.$stickyScrollBar.addClass('grid-sticky-scroll-hidden');
  },

  onClose() {
    if (this.hasStickyFooter) {
      $window.off(`scroll.grid.${this.grid.cid}`);
      this.destroyStickyScrollElement();
    }
  }
});
